import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';

const PLANS = [
  {
    id: 'free',
    label: 'Free',
    price: '$0',
    period: '/mo',
    description: 'Get started',
    features: ['1 estimate / month', 'Basic exports', 'Email support'],
    cta: 'Start Free',
    highlight: false,
  },
  {
    id: 'pro',
    label: 'Pro',
    price: '$249',
    period: '/mo',
    description: 'Most popular',
    features: ['Unlimited estimates', 'PDF + Excel exports', 'RFI generation', 'AI-powered parsing', 'Priority support'],
    cta: 'Start Free Trial',
    highlight: true,
  },
  {
    id: 'business',
    label: 'Business',
    price: '$499',
    period: '/mo',
    description: 'For teams',
    features: ['Everything in Pro', 'Team collaboration', 'Multi-user accounts', 'Custom branding', 'Dedicated support'],
    cta: 'Start Free Trial',
    highlight: false,
  },
];

const SignupPage = ({ onSwitchToLogin, defaultPlan = 'pro' }) => {
  const { signUp } = useAuth();
  const [selectedPlan, setSelectedPlan] = useState(defaultPlan);
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password.length < 8) {
      setError('Password must be at least 8 characters.');
      return;
    }
    setLoading(true);
    setError('');

    const { data, error: signUpError } = await signUp(email, password, fullName);
    if (signUpError) {
      setError(signUpError.message);
      setLoading(false);
      return;
    }

    // If plan is paid, kick off Stripe checkout after account creation
    if (selectedPlan !== 'free' && data?.user) {
      try {
        const response = await fetch('/.netlify/functions/create-checkout-session', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            plan: selectedPlan,
            userId: data.user.id,
            email: email,
          }),
        });
        const { url } = await response.json();
        if (url) {
          window.location.href = url;
          return;
        }
      } catch (err) {
        console.error('Checkout error:', err);
        // Don't block signup — user can upgrade later
      }
    }

    setSuccess(true);
    setLoading(false);
  };

  if (success) {
    return (
      <div style={{
        minHeight: '100vh',
        background: '#0f172a',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '24px',
      }}>
        <div style={{
          background: '#1e293b',
          border: '1px solid #334155',
          borderRadius: '16px',
          padding: '48px 40px',
          maxWidth: '440px',
          width: '100%',
          textAlign: 'center',
        }}>
          <div style={{ fontSize: '48px', marginBottom: '16px' }}>📧</div>
          <h2 style={{ color: '#f1f5f9', fontSize: '22px', fontWeight: '700', marginBottom: '12px' }}>
            Check your email
          </h2>
          <p style={{ color: '#94a3b8', fontSize: '14px', lineHeight: '1.6' }}>
            We sent a confirmation link to <strong style={{ color: '#f1f5f9' }}>{email}</strong>.
            Click it to activate your account, then sign in.
          </p>
          <button
            onClick={onSwitchToLogin}
            style={{
              marginTop: '28px',
              background: '#dc2626',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              padding: '11px 28px',
              fontSize: '14px',
              fontWeight: '700',
              cursor: 'pointer',
            }}
          >
            Go to sign in
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={{
      minHeight: '100vh',
      background: '#0f172a',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '24px',
    }}>
      {/* Logo */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '40px' }}>
        <div style={{
          background: '#dc2626',
          width: '36px',
          height: '36px',
          borderRadius: '8px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: '18px',
          fontWeight: '800',
          color: 'white',
        }}>C</div>
        <span style={{ color: '#f1f5f9', fontWeight: '700', fontSize: '20px', letterSpacing: '-0.3px' }}>
          ConstructPro
        </span>
      </div>

      <div style={{ width: '100%', maxWidth: '800px' }}>
        <h1 style={{
          color: '#f1f5f9',
          fontSize: '24px',
          fontWeight: '700',
          marginBottom: '6px',
          textAlign: 'center',
        }}>
          Create your account
        </h1>
        <p style={{
          color: '#94a3b8',
          fontSize: '14px',
          textAlign: 'center',
          marginBottom: '36px',
        }}>
          Choose a plan and get started in minutes
        </p>

        {/* Plan Selector */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(3, 1fr)',
          gap: '16px',
          marginBottom: '36px',
        }}>
          {PLANS.map((plan) => (
            <div
              key={plan.id}
              onClick={() => setSelectedPlan(plan.id)}
              style={{
                background: selectedPlan === plan.id ? 'rgba(220,38,38,0.1)' : '#1e293b',
                border: selectedPlan === plan.id ? '2px solid #dc2626' : '2px solid #334155',
                borderRadius: '12px',
                padding: '20px',
                cursor: 'pointer',
                transition: 'all 0.15s',
                position: 'relative',
              }}
            >
              {plan.highlight && (
                <div style={{
                  position: 'absolute',
                  top: '-10px',
                  left: '50%',
                  transform: 'translateX(-50%)',
                  background: '#dc2626',
                  color: 'white',
                  fontSize: '10px',
                  fontWeight: '700',
                  padding: '3px 10px',
                  borderRadius: '20px',
                  letterSpacing: '0.5px',
                  textTransform: 'uppercase',
                  whiteSpace: 'nowrap',
                }}>
                  Most Popular
                </div>
              )}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
                <span style={{ color: '#f1f5f9', fontWeight: '700', fontSize: '15px' }}>{plan.label}</span>
                <div style={{
                  width: '18px',
                  height: '18px',
                  borderRadius: '50%',
                  border: selectedPlan === plan.id ? '2px solid #dc2626' : '2px solid #475569',
                  background: selectedPlan === plan.id ? '#dc2626' : 'transparent',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}>
                  {selectedPlan === plan.id && (
                    <div style={{ width: '8px', height: '8px', background: 'white', borderRadius: '50%' }} />
                  )}
                </div>
              </div>
              <div style={{ marginBottom: '14px' }}>
                <span style={{ color: '#f1f5f9', fontSize: '26px', fontWeight: '800' }}>{plan.price}</span>
                <span style={{ color: '#64748b', fontSize: '13px' }}>{plan.period}</span>
              </div>
              <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                {plan.features.map((f, i) => (
                  <li key={i} style={{
                    color: '#94a3b8',
                    fontSize: '12px',
                    padding: '3px 0',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                  }}>
                    <span style={{ color: '#22c55e', fontSize: '10px' }}>✓</span> {f}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {/* Signup Form */}
        <div style={{
          background: '#1e293b',
          border: '1px solid #334155',
          borderRadius: '16px',
          padding: '36px 40px',
        }}>
          {error && (
            <div style={{
              background: 'rgba(220,38,38,0.12)',
              border: '1px solid rgba(220,38,38,0.3)',
              borderRadius: '8px',
              padding: '12px 16px',
              marginBottom: '20px',
              color: '#f87171',
              fontSize: '13px',
            }}>
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '20px' }}>
              <div>
                <label style={{
                  display: 'block',
                  color: '#cbd5e1',
                  fontSize: '13px',
                  fontWeight: '600',
                  marginBottom: '8px',
                }}>
                  Full name
                </label>
                <input
                  type="text"
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                  required
                  placeholder="John Smith"
                  style={inputStyle}
                  onFocus={(e) => e.target.style.borderColor = '#dc2626'}
                  onBlur={(e) => e.target.style.borderColor = '#334155'}
                />
              </div>
              <div>
                <label style={{
                  display: 'block',
                  color: '#cbd5e1',
                  fontSize: '13px',
                  fontWeight: '600',
                  marginBottom: '8px',
                }}>
                  Email address
                </label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  placeholder="you@company.com"
                  style={inputStyle}
                  onFocus={(e) => e.target.style.borderColor = '#dc2626'}
                  onBlur={(e) => e.target.style.borderColor = '#334155'}
                />
              </div>
            </div>

            <div style={{ marginBottom: '28px' }}>
              <label style={{
                display: 'block',
                color: '#cbd5e1',
                fontSize: '13px',
                fontWeight: '600',
                marginBottom: '8px',
              }}>
                Password
              </label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                minLength={8}
                placeholder="Min. 8 characters"
                style={inputStyle}
                onFocus={(e) => e.target.style.borderColor = '#dc2626'}
                onBlur={(e) => e.target.style.borderColor = '#334155'}
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              style={{
                width: '100%',
                background: loading ? '#991b1b' : '#dc2626',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                padding: '13px',
                fontSize: '15px',
                fontWeight: '700',
                cursor: loading ? 'not-allowed' : 'pointer',
                transition: 'background 0.15s',
              }}
              onMouseOver={(e) => { if (!loading) e.target.style.background = '#b91c1c'; }}
              onMouseOut={(e) => { if (!loading) e.target.style.background = '#dc2626'; }}
            >
              {loading
                ? 'Creating account…'
                : selectedPlan === 'free'
                  ? 'Create free account'
                  : `Start free trial → ${PLANS.find(p => p.id === selectedPlan)?.label} plan`}
            </button>

            <p style={{ color: '#475569', fontSize: '12px', textAlign: 'center', marginTop: '14px', lineHeight: '1.5' }}>
              By creating an account you agree to our Terms of Service.
              {selectedPlan !== 'free' && ' Card required only after free trial ends.'}
            </p>
          </form>
        </div>

        <p style={{
          color: '#64748b',
          fontSize: '13px',
          textAlign: 'center',
          marginTop: '24px',
        }}>
          Already have an account?{' '}
          <button
            onClick={onSwitchToLogin}
            style={{
              background: 'none',
              border: 'none',
              color: '#f87171',
              fontWeight: '600',
              cursor: 'pointer',
              fontSize: '13px',
              padding: 0,
            }}
          >
            Sign in
          </button>
        </p>
      </div>

      <p style={{ color: '#334155', fontSize: '12px', marginTop: '32px' }}>
        © 2025 ConstructPro. All rights reserved.
      </p>
    </div>
  );
};

const inputStyle = {
  width: '100%',
  background: '#0f172a',
  border: '1px solid #334155',
  borderRadius: '8px',
  padding: '10px 14px',
  color: '#f1f5f9',
  fontSize: '14px',
  outline: 'none',
  boxSizing: 'border-box',
  transition: 'border-color 0.15s',
};

export default SignupPage;
